import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme';

import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import StockList from './pages/StockList';
import Disposables from './pages/Disposables';
import Implants from './pages/Implants';
import AddItem from './pages/AddItem';
import Vendors from './pages/Vendors';
import Reports from './pages/Reports';
import InstrumentReports from './pages/InstrumentReports';
import EquipmentReports from './pages/EquipmentReports';
import Instruments from './pages/Instruments';
import Equipment from './pages/Equipment';
import {
  MaterialDetailPage,
  DisposableDetailPage,
  ImplantDetailPage,
  InstrumentDetailPage,
  EquipmentDetailPage
} from './pages/StockDetailPages';
import PageShell from './components/PageShell';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? <Navigate to="/stock" /> : children;
};

const AppContent = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <PageShell>
              <Login />
            </PageShell>
          </PublicRoute>
        } />
        <Route path="/signup" element={
          <PublicRoute>
            <PageShell>
              <Signup />
            </PageShell>
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <PageShell>
              <ForgotPassword />
            </PageShell>
          </PublicRoute>
        } />
        <Route path="/stock" element={
          <ProtectedRoute>
            <PageShell withNav>
              <StockList />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/stock/:materialName" element={
          <ProtectedRoute>
            <PageShell>
              <MaterialDetailPage />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/disposables" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Disposables />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/disposables/:id" element={
          <ProtectedRoute>
            <PageShell>
              <DisposableDetailPage />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/implants" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Implants />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/implants/:implantName" element={
          <ProtectedRoute>
            <PageShell>
              <ImplantDetailPage />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/add-item" element={
          <ProtectedRoute>
            <PageShell withNav>
              <AddItem />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/vendors" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Vendors />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Reports />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/reports/instruments" element={
          <ProtectedRoute>
            <PageShell withNav>
              <InstrumentReports />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/reports/equipment" element={
          <ProtectedRoute>
            <PageShell withNav>
              <EquipmentReports />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/instruments" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Instruments />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/instruments/:instrumentName" element={
          <ProtectedRoute>
            <PageShell>
              <InstrumentDetailPage />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/equipment" element={
          <ProtectedRoute>
            <PageShell withNav>
              <Equipment />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/equipment/:id" element={
          <ProtectedRoute>
            <PageShell>
              <EquipmentDetailPage />
            </PageShell>
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/stock" />} />
      </Routes>
    </Router>
  );
};

const App = () => {
  return (
    <CssVarsProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </CssVarsProvider>
  );
};

export default App;
